import React, { useEffect, useRef, useState } from 'react';
import { SCREENS } from './screens';
import type { SyncPeer, SyncRole } from './cockpitSync';

/**
 * Bloc « Multi-écrans » de la topbar — port fidèle du prototype
 * (`index.html` .screens + `app.js` renderScreensUI / buildScreenMenu).
 * Affiche le rôle de cette fenêtre (Maître / Réplique), le nombre d'écrans
 * connectés, et un menu pour ouvrir une fenêtre secondaire verrouillée sur un
 * module + suivre (ou non) la sélection/le périmètre des autres écrans.
 */

interface Props {
  role: SyncRole;
  screenCount: number;
  currentScreen: string;
  peers: [string, SyncPeer][];
  selfId: string;
  available: boolean;
  follow: boolean;
  onToggleFollow: () => void;
  onOpenScreen: (mode: string) => void;
}

const ScreensControl: React.FC<Props> = ({
  role,
  screenCount,
  currentScreen,
  peers,
  selfId,
  available,
  follow,
  onToggleFollow,
  onOpenScreen,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const maitre = role === 'master';

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="screens" ref={ref}>
      <button
        className={`screens__role ${maitre ? 'is-master' : ''} ${!follow ? 'is-detached' : ''}`}
        type="button"
        title={
          maitre
            ? `Cette fenêtre porte l'état de référence · ${screenCount} écran(s) connecté(s)`
            : `Cette fenêtre affiche l'état du maître · ${screenCount} écran(s) connecté(s)`
        }
        onClick={() => setOpen((v) => !v)}
      >
        <span className="screens__dot" />
        <span>{maitre ? 'Maître' : 'Réplique'}</span>
        <span className="screens__count mono">{screenCount}</span>
      </button>
      <button
        className="screens__add"
        type="button"
        title="Ouvrir un écran secondaire"
        onClick={() => setOpen((v) => !v)}
      >
        <svg viewBox="0 0 24 24"><path d="M3 4h12v9H3V4Zm14 5h4v11h-9v-4h5V9ZM5 6v5h8V6H5Z" /></svg>
        <span>Écran</span>
      </button>

      {open ? (
        <div className="screens__menu">
          <p className="smenu__h">Ouvrir un écran secondaire</p>
          {Object.entries(SCREENS).map(([k, s]) => (
            <button
              key={k}
              className="sitem"
              type="button"
              onClick={() => {
                onOpenScreen(k);
                setOpen(false);
              }}
            >
              <span className="sitem__ind mono">{k === currentScreen ? '•' : ' '}</span>
              <span className="sitem__body">
                <span className="sitem__nom">{s.nom}</span>
                <span className="sitem__sub">{s.desc}</span>
              </span>
            </button>
          ))}

          <div className="sitem__sep" />
          <label className="smenu__follow">
            <input type="checkbox" checked={follow} onChange={onToggleFollow} />
            <span>Suivre la sélection et le périmètre des autres écrans</span>
          </label>

          <div className="sitem__sep" />
          <p className="smenu__h">Écrans connectés ({screenCount})</p>
          <div className="smenu__peer">
            <span className={`cbadge ${maitre ? 'cbadge--on' : 'cbadge--hab'}`}>{maitre ? 'Maître' : 'Réplique'}</span>
            <span className="mono">{selfId}</span>
            <span className="muted">{SCREENS[currentScreen]?.nom || currentScreen} — cette fenêtre</span>
          </div>
          {peers.length ? (
            peers.map(([id, p]) => (
              <div className="smenu__peer" key={id}>
                <span className={`cbadge ${p.role === 'master' ? 'cbadge--on' : 'cbadge--hab'}`}>
                  {p.role === 'master' ? 'Maître' : 'Réplique'}
                </span>
                <span className="mono">{id}</span>
                <span className="muted">{SCREENS[p.screen]?.nom || p.screen}</span>
              </div>
            ))
          ) : (
            <p className="smenu__empty">Aucun autre écran. Ouvrez-en un ci-dessus.</p>
          )}
          {available ? null : (
            <p className="smenu__warn">⚠ Synchronisation indisponible dans ce navigateur.</p>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default ScreensControl;
